import React, { useState, useEffect } from 'react';
import { receivableService } from '../services/receivableService';
import { Receivable, ReceivableDashboard, ReceivableHistory } from '../types';
import { formatCurrency, formatDate } from '../utils/formatters';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import Alert from '../components/common/Alert';
import {
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  Search,
  Filter,
  X,
  Eye,
  Download,
  XCircle,
  ToggleLeft,
  ToggleRight,
  Calendar,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import Swal from 'sweetalert2';

const Receivables: React.FC = () => {
  const [receivables, setReceivables] = useState<any>(null);
  const [dashboard, setDashboard] = useState<ReceivableDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReceivable, setSelectedReceivable] = useState<Receivable | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [history, setHistory] = useState<ReceivableHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Filtros
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    associate_name: '',
    start_date: '',
    end_date: '',
    payment_method: '',
    active: 'true',
    page: 1,
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadData();
  }, [filters]);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Preparar filtros convertendo active de string para boolean
      const apiFilters = {
        ...filters,
        active: filters.active === 'true' ? true : filters.active === 'false' ? false : undefined,
      };

      const data = await receivableService.getAll(apiFilters);
      setReceivables(data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar parcelas');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    try {
      const stats = await receivableService.getDashboard();
      setDashboard(stats);
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
    }
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      search: '',
      associate_name: '',
      start_date: '',
      end_date: '',
      payment_method: '',
      active: 'true',
      page: 1,
    });
  };

  const hasActiveFilters = () => {
    return filters.status !== '' ||
           filters.search !== '' ||
           filters.associate_name !== '' ||
           filters.start_date !== '' ||
           filters.end_date !== '' ||
           filters.payment_method !== '' ||
           filters.active !== 'true';
  };

  const handleMarkAsPaid = async (receivable: Receivable) => {
    const { value: formValues } = await Swal.fire({
      title: 'Baixar Parcela',
      html: `
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Valor Pago</label>
            <input id="amount_paid" type="number" step="0.01" value="${receivable.amount}" class="w-full px-3 py-2 border rounded-md" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Data do Pagamento</label>
            <input id="payment_date" type="date" value="${new Date().toISOString().split('T')[0]}" class="w-full px-3 py-2 border rounded-md" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento <span class="text-red-500">*</span></label>
            <select id="payment_method" class="w-full px-3 py-2 border rounded-md" required>
              <option value="">Selecione...</option>
              <option value="dinheiro">Dinheiro</option>
              <option value="pix">PIX</option>
              <option value="cartao_credito">Cartão de Crédito</option>
              <option value="cartao_debito">Cartão de Débito</option>
              <option value="transferencia">Transferência Bancária</option>
              <option value="boleto">Boleto</option>
              <option value="cheque">Cheque</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Observações</label>
            <textarea id="notes" class="w-full px-3 py-2 border rounded-md" rows="3"></textarea>
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Baixar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
      preConfirm: () => {
        const paymentMethod = (document.getElementById('payment_method') as HTMLSelectElement).value;
        if (!paymentMethod) {
          Swal.showValidationMessage('Por favor, selecione a forma de pagamento');
          return false;
        }
        return {
          amount_paid: parseFloat((document.getElementById('amount_paid') as HTMLInputElement).value),
          payment_date: (document.getElementById('payment_date') as HTMLInputElement).value,
          payment_method: paymentMethod,
          notes: (document.getElementById('notes') as HTMLTextAreaElement).value,
        };
      },
    });

    if (formValues) {
      try {
        await receivableService.markAsPaid(receivable.id, formValues);
        Swal.fire('Sucesso!', 'Parcela baixada com sucesso', 'success');
        loadData();
        loadDashboard();
      } catch (err: any) {
        Swal.fire('Erro!', err.response?.data?.message || 'Erro ao baixar parcela', 'error');
      }
    }
  };

  const handleCancelPayment = async (receivable: Receivable) => {
    const { value: notes } = await Swal.fire({
      title: 'Cancelar Baixa',
      input: 'textarea',
      inputLabel: 'Motivo do cancelamento',
      inputPlaceholder: 'Digite o motivo...',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
    });

    if (notes !== undefined) {
      try {
        await receivableService.cancelPayment(receivable.id, notes);
        Swal.fire('Sucesso!', 'Baixa cancelada com sucesso', 'success');
        loadData();
        loadDashboard();
      } catch (err: any) {
        Swal.fire('Erro!', err.response?.data?.message || 'Erro ao cancelar baixa', 'error');
      }
    }
  };

  const handleInactivate = async (receivable: Receivable) => {
    const { value: notes } = await Swal.fire({
      title: 'Inativar Parcela',
      input: 'textarea',
      inputLabel: 'Motivo da inativação (obrigatório)',
      inputPlaceholder: 'Digite o motivo...',
      showCancelButton: true,
      confirmButtonText: 'Inativar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#f59e0b',
      inputValidator: (value) => {
        if (!value) {
          return 'Você precisa informar um motivo!';
        }
      },
    });

    if (notes) {
      try {
        await receivableService.inactivate(receivable.id, notes);
        Swal.fire('Sucesso!', 'Parcela inativada com sucesso', 'success');
        loadData();
        loadDashboard(); // Recarregar estatísticas
      } catch (err: any) {
        Swal.fire('Erro!', err.response?.data?.message || 'Erro ao inativar parcela', 'error');
      }
    }
  };

  const handleReactivate = async (receivable: Receivable) => {
    const result = await Swal.fire({
      title: 'Reativar Parcela',
      text: 'Deseja reativar esta parcela?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, reativar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
    });

    if (result.isConfirmed) {
      try {
        await receivableService.reactivate(receivable.id);
        Swal.fire('Sucesso!', 'Parcela reativada com sucesso', 'success');
        loadData();
        loadDashboard(); // Recarregar estatísticas
      } catch (err: any) {
        Swal.fire('Erro!', err.response?.data?.message || 'Erro ao reativar parcela', 'error');
      }
    }
  };

  const handleShowHistory = async (receivable: Receivable) => {
    setSelectedReceivable(receivable);
    setShowHistoryModal(true);
    setLoadingHistory(true);

    try {
      const historyData = await receivableService.getHistory(receivable.id);
      setHistory(historyData);
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pendente', icon: Clock },
      paid: { color: 'bg-green-100 text-green-800', label: 'Pago', icon: CheckCircle },
      overdue: { color: 'bg-red-100 text-red-800', label: 'Vencido', icon: AlertCircle },
      cancelled: { color: 'bg-gray-100 text-gray-800', label: 'Cancelado', icon: XCircle },
    };

    const badge = badges[status as keyof typeof badges] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const getActionLabel = (action: string) => {
    const labels = {
      created: 'Criado',
      paid: 'Baixado',
      payment_cancelled: 'Baixa Cancelada',
      inactivated: 'Inativado',
      reactivated: 'Reativado',
      updated: 'Atualizado',
    };
    return labels[action as keyof typeof labels] || action;
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels = {
      dinheiro: 'Dinheiro',
      pix: 'PIX',
      cartao_credito: 'Cartão de Crédito',
      cartao_debito: 'Cartão de Débito',
      transferencia: 'Transferência',
      boleto: 'Boleto',
      cheque: 'Cheque',
    };
    return labels[method as keyof typeof labels] || method;
  };

  if (loading && !receivables) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contas a Receber</h1>
          <p className="text-gray-600 mt-1">Gestão completa de parcelas e recebimentos</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <Alert type="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Dashboard Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">A Receber</p>
                <p className="text-2xl font-bold text-yellow-900 mt-1">
                  {formatCurrency(dashboard.total_pending)}
                </p>
                <p className="text-xs text-yellow-600 mt-1">{dashboard.count_pending} parcelas</p>
              </div>
              <div className="bg-yellow-200 p-3 rounded-full">
                <Clock className="w-6 h-6 text-yellow-700" />
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">Vencidas</p>
                <p className="text-2xl font-bold text-red-900 mt-1">
                  {formatCurrency(dashboard.total_overdue)}
                </p>
                <p className="text-xs text-red-600 mt-1">{dashboard.count_overdue} parcelas</p>
              </div>
              <div className="bg-red-200 p-3 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-700" />
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Recebido (Mês)</p>
                <p className="text-2xl font-bold text-green-900 mt-1">
                  {formatCurrency(dashboard.total_paid)}
                </p>
                <p className="text-xs text-green-600 mt-1">{dashboard.count_paid} parcelas</p>
              </div>
              <div className="bg-green-200 p-3 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-700" />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Search className="w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por referência..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="border-0 focus:ring-0 p-0 text-sm"
              />
            </div>
            <div className="flex items-center gap-2 border-l pl-4">
              <Search className="w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome ou CPF do associado..."
                value={filters.associate_name}
                onChange={(e) => setFilters({ ...filters, associate_name: e.target.value, page: 1 })}
                className="border-0 focus:ring-0 p-0 text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2">
            {hasActiveFilters() && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="text-red-600 hover:bg-red-50"
              >
                <X className="w-4 h-4 mr-2" />
                Limpar Filtros
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtros
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 pt-4 border-t">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                className="w-full rounded-md border-gray-300"
              >
                <option value="">Todos</option>
                <option value="pending">Pendente</option>
                <option value="paid">Pago</option>
                <option value="overdue">Vencido</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value, page: 1 })}
                className="w-full rounded-md border-gray-300"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value, page: 1 })}
                className="w-full rounded-md border-gray-300"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento</label>
              <select
                value={filters.payment_method}
                onChange={(e) => setFilters({ ...filters, payment_method: e.target.value, page: 1 })}
                className="w-full rounded-md border-gray-300"
              >
                <option value="">Todas</option>
                <option value="dinheiro">Dinheiro</option>
                <option value="pix">PIX</option>
                <option value="cartao_credito">Cartão de Crédito</option>
                <option value="cartao_debito">Cartão de Débito</option>
                <option value="transferencia">Transferência</option>
                <option value="boleto">Boleto</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Situação</label>
              <select
                value={filters.active}
                onChange={(e) => setFilters({ ...filters, active: e.target.value, page: 1 })}
                className="w-full rounded-md border-gray-300"
              >
                <option value="true">Ativas</option>
                <option value="false">Inativas</option>
                <option value="">Todas</option>
              </select>
            </div>
          </div>
        )}
      </Card>

      {/* Error */}
      {error && <Alert type="error" message={error} />}

      {/* Mobile: Cards */}
      <div className="md:hidden space-y-4">
        {receivables?.data?.map((receivable: Receivable) => (
          <Card key={receivable.id} className={!receivable.active ? 'opacity-60' : ''}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-medium text-gray-900">{receivable.reference_number}</div>
                {receivable.is_overdue && (
                  <div className="text-xs text-red-600">{receivable.days_overdue} dias de atraso</div>
                )}
              </div>
              {getStatusBadge(receivable.status)}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm mb-3">
              <div>
                <p className="text-gray-500">Associado</p>
                <p className="font-medium text-gray-900">{receivable.associate?.nome || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">Plano</p>
                <p className="font-medium text-gray-900">{receivable.plan?.nome || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">Vencimento</p>
                <p className="font-medium text-gray-900">{formatDate(receivable.due_date)}</p>
                {receivable.payment_date && (
                  <p className="text-xs text-green-600">Pago em {formatDate(receivable.payment_date)}</p>
                )}
              </div>
              <div>
                <p className="text-gray-500">Valor</p>
                <p className="font-semibold text-gray-900">{formatCurrency(receivable.amount)}</p>
                {receivable.amount_paid > 0 && receivable.amount_paid !== receivable.amount && (
                  <p className="text-xs text-gray-500">Pago: {formatCurrency(receivable.amount_paid)}</p>
                )}
                {receivable.payment_method && (
                  <p className="text-xs text-blue-600">{getPaymentMethodLabel(receivable.payment_method)}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => handleShowHistory(receivable)}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-900"
                title="Ver histórico"
              >
                <Eye className="w-4 h-4" />
                <span>Histórico</span>
              </button>

              {receivable.active && receivable.status === 'pending' && (
                <button
                  type="button"
                  onClick={() => handleMarkAsPaid(receivable)}
                  className="flex items-center gap-1 text-sm text-green-600 hover:text-green-900"
                  title="Baixar parcela"
                >
                  <Download className="w-4 h-4" />
                  <span>Baixar</span>
                </button>
              )}

              {receivable.active && receivable.status === 'paid' && (
                <button
                  type="button"
                  onClick={() => handleCancelPayment(receivable)}
                  className="flex items-center gap-1 text-sm text-orange-600 hover:text-orange-900"
                  title="Cancelar baixa"
                >
                  <X className="w-4 h-4" />
                  <span>Cancelar</span>
                </button>
              )}

              {receivable.active ? (
                <button
                  type="button"
                  onClick={() => handleInactivate(receivable)}
                  className="flex items-center gap-1 text-sm text-red-600 hover:text-red-900 ml-auto"
                  title="Inativar"
                >
                  <ToggleRight className="w-4 h-4" />
                  <span>Inativar</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleReactivate(receivable)}
                  className="flex items-center gap-1 text-sm text-green-600 hover:text-green-900 ml-auto"
                  title="Reativar"
                >
                  <ToggleLeft className="w-4 h-4" />
                  <span>Reativar</span>
                </button>
              )}
            </div>
          </Card>
        ))}

        {receivables?.data?.length === 0 && (
          <div className="text-center py-12">
            <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">Nenhuma parcela encontrada</p>
          </div>
        )}

        {/* Pagination Mobile */}
        {receivables?.last_page > 1 && (
          <div className="flex flex-col items-center gap-3 pt-4">
            <div className="text-sm text-gray-700">
              {receivables.from} a {receivables.to} de {receivables.total}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={receivables.current_page === 1}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={receivables.current_page === receivables.last_page}
              >
                Próxima
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Desktop: Table */}
      <Card className="hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Referência
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Associado / Plano
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vencimento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {receivables?.data?.map((receivable: Receivable) => (
                <tr key={receivable.id} className={!receivable.active ? 'bg-gray-50 opacity-60' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{receivable.reference_number}</div>
                    {receivable.is_overdue && (
                      <div className="text-xs text-red-600">{receivable.days_overdue} dias de atraso</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{receivable.associate?.nome}</div>
                    <div className="text-xs text-gray-500">{receivable.plan?.nome}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(receivable.due_date)}</div>
                    {receivable.payment_date && (
                      <div className="text-xs text-green-600">Pago em {formatDate(receivable.payment_date)}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">{formatCurrency(receivable.amount)}</div>
                    {receivable.amount_paid > 0 && receivable.amount_paid !== receivable.amount && (
                      <div className="text-xs text-gray-500">Pago: {formatCurrency(receivable.amount_paid)}</div>
                    )}
                    {receivable.payment_method && (
                      <div className="text-xs text-blue-600">
                        {getPaymentMethodLabel(receivable.payment_method)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(receivable.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleShowHistory(receivable)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Ver histórico"
                      >
                        <Eye className="w-4 h-4" />
                      </button>

                      {receivable.active && receivable.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => handleMarkAsPaid(receivable)}
                          className="text-green-600 hover:text-green-900"
                          title="Baixar parcela"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      )}

                      {receivable.active && receivable.status === 'paid' && (
                        <button
                          type="button"
                          onClick={() => handleCancelPayment(receivable)}
                          className="text-orange-600 hover:text-orange-900"
                          title="Cancelar baixa"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}

                      {receivable.active ? (
                        <button
                          type="button"
                          onClick={() => handleInactivate(receivable)}
                          className="text-red-600 hover:text-red-900"
                          title="Inativar"
                        >
                          <ToggleRight className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleReactivate(receivable)}
                          className="text-green-600 hover:text-green-900"
                          title="Reativar"
                        >
                          <ToggleLeft className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {receivables?.data?.length === 0 && (
            <div className="text-center py-12">
              <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Nenhuma parcela encontrada</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {receivables?.last_page > 1 && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando {receivables.from} a {receivables.to} de {receivables.total} registros
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={receivables.current_page === 1}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={receivables.current_page === receivables.last_page}
              >
                Próxima
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* History Modal */}
      {showHistoryModal && selectedReceivable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Histórico - {selectedReceivable.reference_number}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedReceivable.associate?.nome} - {selectedReceivable.plan?.nome}
                </p>
              </div>
              <button
                type="button"
                title="Fechar"
                onClick={() => setShowHistoryModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : history.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nenhum histórico disponível</p>
              ) : (
                <div className="space-y-4">
                  {history.map((item) => (
                    <div key={item.id} className="border-l-4 border-blue-500 pl-4 py-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{getActionLabel(item.action)}</p>
                          <p className="text-sm text-gray-600 mt-1">
                            Por: {item.user?.name || 'Sistema'}
                          </p>
                          {item.notes && (
                            <p className="text-sm text-gray-500 mt-1 italic">{item.notes}</p>
                          )}
                          {item.amount && (
                            <p className="text-sm text-gray-700 mt-1">Valor: {formatCurrency(item.amount)}</p>
                          )}
                          {item.metadata?.payment_method && (
                            <p className="text-sm text-blue-600 mt-1">
                              Forma de Pagamento: {getPaymentMethodLabel(item.metadata.payment_method)}
                            </p>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">{formatDate(item.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50">
              <Button variant="outline" onClick={() => setShowHistoryModal(false)} className="w-full">
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Receivables;
